import { Autocomplete, type Item } from '@ads/components-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getAutocompleteSearchMessage } from '@/shared/utils/getAutocompleteSearchMessage';
import {
  type StudentEnrollmentOption,
  useStudentEnrollmentSearch,
} from '../../../hooks/useStudentEnrollmentSearch';

export interface StudentSearchAutocompleteProps {
  /** `courseRegistrationId` selecionado; `''` quando nenhum aluno está selecionado. */
  readonly selectedId: string;
  /** Rótulo do aluno selecionado (para exibir a seleção corrente). */
  readonly selectedLabel?: string;
  /** Notifica a matrícula escolhida (`null` ao limpar). */
  readonly onSelect: (option: StudentEnrollmentOption | null) => void;
  readonly label: string;
  readonly placeholder: string;
}

/**
 * Autocomplete de matrículas de alunos com busca por texto (debounce + mínimo de 3
 * caracteres), espelhando o {@link TurmaAutocomplete}. Consome
 * {@link useStudentEnrollmentSearch} (endpoint real `person/shared/student-enrollments`).
 */
export function StudentSearchAutocomplete({
  selectedId,
  selectedLabel = '',
  onSelect,
  label,
  placeholder,
}: StudentSearchAutocompleteProps) {
  const { t: tCommon } = useTranslation('common');
  const [search, setSearch] = useState('');

  const { students, isQuerySuccess } = useStudentEnrollmentSearch(search);

  const items = students.map(
    (s): Item<string> => ({ label: s.label, value: s.courseRegistrationId }),
  );
  const selectedItem: Item<string> = selectedId
    ? { label: selectedLabel, value: selectedId }
    : { label: '', value: '' };

  const notFoundMessage = getAutocompleteSearchMessage(
    search.length,
    isQuerySuccess,
    items.length,
    {
      minChars: tCommon('search.minChars'),
      searching: tCommon('search.searching'),
      notFound: tCommon('search.notFound'),
    },
  );

  return (
    <Autocomplete<string>
      label={label}
      placeholder={placeholder}
      size="md"
      list={items}
      custom={{ input: '!w-full' }}
      selectedItem={selectedItem}
      {...(notFoundMessage !== undefined ? { notFoundMessage } : {})}
      onChange={e => setSearch(e.target.value)}
      handleChange={item => {
        const found = students.find(s => s.courseRegistrationId === item.value);
        onSelect(found ?? null);
        setSearch('');
      }}
      handleClearQuery={() => {
        onSelect(null);
        setSearch('');
      }}
    />
  );
}
